import React, { CSSProperties, useEffect, useRef } from "react";

export type AdSenseUnitName =
  | "desktop_top_leaderboard"
  | "desktop_rail_300x600"
  | "desktop_rail_160x600"
  | "desktop_incontent_728x90"
  | "desktop_incontent_flex"
  | "mobile_top_banner"
  | "mobile_inline_banner"
  | "mobile_inline_banner_compact"
  | "content_mpu";

type ResponsiveFormat = "horizontal" | "rectangle" | "rectangle,horizontal";

interface AdSenseUnitConfig {
  slot: string;
  format?: ResponsiveFormat;
  fullWidthResponsive?: boolean;
  width?: number;
  height?: number;
  minHeight: number;
  maxWidth: number;
}

export const ADSENSE_CLIENT = "ca-pub-1478329345834248";

export const ADSENSE_UNITS: Record<AdSenseUnitName, AdSenseUnitConfig> = {
  desktop_top_leaderboard: {
    slot: "9547922185",
    format: "horizontal",
    minHeight: 90,
    maxWidth: 970,
  },
  desktop_rail_300x600: {
    slot: "3824702075",
    width: 300,
    height: 600,
    minHeight: 600,
    maxWidth: 300,
  },
  desktop_rail_160x600: {
    slot: "2184317348",
    width: 160,
    height: 600,
    minHeight: 600,
    maxWidth: 160,
  },
  desktop_incontent_728x90: {
    slot: "9412190812",
    format: "horizontal",
    minHeight: 90,
    maxWidth: 728,
  },
  // Not a new AdSense unit: the same slot as `desktop_incontent_728x90`, so it
  // shares its reporting line. The declared 728x90 is the one shape these
  // containers cannot hold. The unit was measured serving at 622x100 on a
  // 1280px viewport, and the centre column between the rails is 302px at a
  // 912px viewport, 414px at 1024px and 670px at 1280px, so 728 never fits
  // below roughly 1390px. 300x250 and 336x280 fit at every one of those
  // widths, and desktop `content_mpu` — the only desktop box on the site —
  // runs 0.693 coverage against this unit's 0.408. Horizontal stays in the
  // list as a fallback so a banner can still fill when no box is available.
  // The contracts table keeps the 90px entry above: see the change log.
  desktop_incontent_flex: {
    slot: "9412190812",
    format: "rectangle,horizontal",
    // Same reason `content_mpu` and `mobile_inline_banner` set this: full-width
    // responsive lets AdSense stretch the <ins> to the whole 728px column and
    // centre a smaller creative inside it, so a 336-wide box arrives as a
    // 728-wide frame with dead space either side. Measured serving 728x280 with
    // this true. False makes AdSense pick a discrete size that fits instead.
    fullWidthResponsive: false,
    minHeight: 250,
    // Hug the box. At 728 the <ins> stays 728 wide whatever serves, so a
    // 336-wide rectangle arrived as a 728-wide slot with ~350px of dead space
    // and the AdChoices marker floating in it — measured on `/` at a 1408px
    // viewport. 336 is `content_mpu`'s width, the site's best-filling unit at
    // 0.693 desktop, and it still admits 300x250, 336x280 and a 320x100
    // banner. What it gives up is 728x90 and 468x60, and 728x90 was never
    // servable in these columns anyway — that is why this entry exists.
    maxWidth: 336,
  },
  mobile_top_banner: {
    slot: "7763947081",
    format: "horizontal",
    fullWidthResponsive: false,
    height: 100,
    minHeight: 100,
    maxWidth: 768,
  },
  // In-content mobile slot. Pinned to a 100px horizontal banner this was the
  // worst-filling unit on the site (0.356 coverage over 24 Jul - 9 Aug, against
  // 0.695 for the rectangle content_mpu on the same traffic): a 100px letterbox
  // cannot hold 300x250, which is where the mobile demand is. Its placements
  // measure 325-375px wide, so the box always had room — only the declared
  // shape was stopping it. Horizontal stays in the list as a fallback so a
  // banner can still fill when no box is available.
  mobile_inline_banner: {
    slot: "4810480680",
    format: "rectangle,horizontal",
    fullWidthResponsive: false,
    minHeight: 250,
    maxWidth: 768,
  },
  // Not a new AdSense unit: the same slot as `mobile_inline_banner`, so it
  // shares its reporting line. It keeps the original 100px letterbox shape for
  // the league table pages, where a 250px box pushes the table too far down the
  // screen. Everywhere else the in-content mobile slot uses the box.
  mobile_inline_banner_compact: {
    slot: "4810480680",
    format: "horizontal",
    fullWidthResponsive: false,
    height: 100,
    minHeight: 100,
    maxWidth: 768,
  },
  content_mpu: {
    slot: "4295595504",
    format: "rectangle",
    // Keep MPUs inside padded mobile content rails. Letting this unit use
    // AdSense's full-width responsive mode makes it expand to the viewport
    // width, beyond the single-column grid.
    fullWidthResponsive: false,
    minHeight: 250,
    maxWidth: 336,
  },
};

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

interface AdSenseUnitProps {
  unitName: AdSenseUnitName;
  className?: string;
}

export const getAdSenseUnitMinHeight = (unitName: AdSenseUnitName) =>
  ADSENSE_UNITS[unitName].minHeight;

export const AdSenseUnit: React.FC<AdSenseUnitProps> = ({
  unitName,
  className,
}) => {
  const requestedRef = useRef(false);
  const config = ADSENSE_UNITS[unitName];
  const isLocalPreview =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  useEffect(() => {
    if (requestedRef.current) return;

    requestedRef.current = true;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // Ad blockers, localhost, or a delayed loader must not break the page.
    }
  }, []);

  const adStyle: CSSProperties =
    config.width && config.height
      ? {
          display: "inline-block",
          width: config.width,
          height: config.height,
        }
      : {
          display: "block",
          height: config.height,
          width: "100%",
          minHeight: config.minHeight,
        };

  return (
    <div
      className={className}
      data-ad-preview={isLocalPreview ? "true" : undefined}
      data-ad-unit={unitName}
      style={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        margin: "0 auto",
        maxWidth: config.maxWidth,
        minHeight: config.minHeight,
        height: config.height,
        position: "relative",
        width: "100%",
      }}
    >
      {isLocalPreview && (
        <span
          aria-hidden="true"
          style={{
            alignItems: "center",
            background: "rgba(255, 215, 0, 0.06)",
            border: "1px dashed rgba(255, 215, 0, 0.35)",
            color: "rgba(255, 255, 255, 0.65)",
            display: "flex",
            fontFamily: "sans-serif",
            fontSize: 11,
            inset: 0,
            justifyContent: "center",
            letterSpacing: "0.04em",
            pointerEvents: "none",
            position: "absolute",
            textTransform: "uppercase",
          }}
        >
          AdSense preview · {unitName}
        </span>
      )}
      <ins
        key={unitName}
        className="adsbygoogle"
        style={adStyle}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={config.slot}
        data-ad-format={config.format}
        data-full-width-responsive={
          config.format
            ? config.fullWidthResponsive === false
              ? "false"
              : "true"
            : undefined
        }
      />
    </div>
  );
};
